/**
 * Riley chat state that outlives the component that shows it (patchbay#196).
 *
 * Expanding the rail, switching docked/full-size, the mobile sheet and navigating away all remount the rail. Before
 * this, the thread, the pending question and the "Riley is working…" row lived in component state, so a remount
 * dropped the spinner while the request carried on. Now each conversation is an entry in a module-level store keyed
 * by case (or screen), and a turn runs here, not in a component: a remounted rail subscribes to the same entry and
 * picks up the live stream where it is. When the stream itself is lost (network drop, timeout, page reload), the
 * entry polls the stored thread (`GET /chat/{id}`, side-effect free) until the answer lands.
 *
 * Investigate runs the same way (`investigations` below), so an investigation in flight also survives a remount.
 */
import { safeStorage } from '@patchbay/ui'
import * as React from 'react'
import type { ChatDelivery, RileyCard, RileyChatMessage, RileyChatStreamEvent, RileyChatThread, RileyToolStep } from './contracts'
import { announceWalkthrough } from './walkthrough-event'

export type ToolStep = RileyToolStep

/** Turn Riley's internal tool vocabulary into one safe, useful line (#340). Raw arguments/results stay in details. */
export function summarizeToolStep(name: string, argsSummary = '', resultSummary = ''): string {
  const source = `${name} ${argsSummary} ${resultSummary}`.toLowerCase().replace(/[_-]+/g, ' ')
  const relatedCount =
    /\b(\d+)\s+(?:related|similar)\s+tickets?\b/.exec(source)?.[1] ?? /\b(?:related|similar)\s+tickets?\D{0,12}(\d+)\b/.exec(source)?.[1]
  if (/related|similar/.test(source) && /tickets?/.test(source)) {
    return relatedCount ? `Looked at ${relatedCount} related tickets` : 'Looked at related tickets'
  }
  if (/\b(pbx|phone|extension|fs[_-]?pbx|sip|trunk)\b/.test(source)) return 'Checked the phone system records'
  if (/\b(unifi|network|switch|wifi|wireless)\b/.test(source)) return 'Checked the network records'
  if (/\b(ninja|rmm|device|endpoint)\b/.test(source)) return 'Checked device monitoring'
  if (/\b(zabbix|wan)\b/.test(source)) return 'Checked the router WAN status'
  if (/\b(carrier|telnyx|voip\.ms|outage)\b/.test(source)) return 'Checked the carrier status'
  if (/\b(conduit|cmdb|asset|component|system)\b/.test(source)) return 'Checked the system records'
  if (/\b(knowledge|\bkb\b|article)\b/.test(source)) return 'Searched the knowledge base'
  if (/\b(books|invoice|billing|purchase[_ -]?order|\bpo\b)\b/.test(source)) return 'Checked the billing records'
  if (/\b(crm|account|contact|customer)\b/.test(source)) return 'Checked the customer record'
  if (/\b(peek|ticket|thread|desk)\b/.test(source)) return 'Read the ticket'
  if (/\b(search|find|lookup|query)\b/.test(source)) return 'Searched the available records'
  if (/\b(read|get|fetch|open)\b/.test(source)) return 'Read a supporting record'
  return 'Checked a supporting source'
}

export function failedToolStepLabel(label: string): string {
  if (label.startsWith('Read ')) return `Couldn't finish reading ${label.slice(5).toLowerCase()}`
  if (label.startsWith('Checked ')) return `Couldn't finish checking ${label.slice(8).toLowerCase()}`
  if (label.startsWith('Looked at ')) return `Couldn't finish looking at ${label.slice(10).toLowerCase()}`
  if (label.startsWith('Searched ')) return `Couldn't finish searching ${label.slice(9).toLowerCase()}`
  return "Couldn't finish that check"
}

function rawStepDetail(request?: string, result?: string): string | undefined {
  const lines = [request ? `Request: ${request}` : '', result ? `Result: ${result}` : ''].filter(Boolean)
  return lines.length ? lines.join('\n') : undefined
}

export interface PendingTurn {
  text: string
  clientMessageId: string
  threadId: string
  startedAt: number
  /** Messages in the thread before this turn (the server records the question and the answer together). */
  baseline: number
}

export interface RileyChatEntry {
  thread: RileyChatThread | null
  pending: PendingTurn | null
  streamText: string
  toolSteps: ToolStep[]
  error: string | null
  /** The stream is gone; the stored thread is being polled for the answer. */
  recovering: boolean
}

const EMPTY: RileyChatEntry = { thread: null, pending: null, streamText: '', toolSteps: [], error: null, recovering: false }

type Listener = () => void

/** A tiny keyed external store (`useSyncExternalStore`). */
export function createKeyedStore<T>(initial: T) {
  const entries = new Map<string, T>()
  const listeners = new Map<string, Set<Listener>>()
  const get = (key: string): T => entries.get(key) ?? initial
  const set = (key: string, update: (current: T) => T) => {
    const next = update(get(key))
    if (next === get(key)) return
    entries.set(key, next)
    for (const l of listeners.get(key) ?? []) l()
  }
  const subscribe = (key: string, l: Listener) => {
    let s = listeners.get(key)
    if (!s) {
      s = new Set()
      listeners.set(key, s)
    }
    s.add(l)
    return () => {
      s?.delete(l)
    }
  }
  const use = (key: string): T =>
    React.useSyncExternalStore(
      React.useCallback((l: Listener) => subscribe(key, l), [key]),
      () => get(key),
      () => get(key),
    )
  const has = (key: string) => entries.has(key)
  /** Set a first value without notifying (safe during render: nobody has seen this key's value yet). */
  const seed = (key: string, value: T) => {
    if (!entries.has(key)) entries.set(key, value)
  }
  const reset = () => {
    entries.clear()
    for (const s of listeners.values()) for (const l of s) l()
  }
  return { get, set, subscribe, use, has, seed, reset }
}

export const chats = createKeyedStore<RileyChatEntry>(EMPTY)

/** Tests: start every case from an empty store. */
export function resetRileyChatStore(): void {
  chats.reset()
  running.clear()
}

const running = new Set<string>()

export class RileyTurnError extends Error {
  constructor(
    readonly code: string,
    message: string,
  ) {
    super(message)
    this.name = 'RileyTurnError'
  }
}

/** A refusal the server answered (HTTP status or a stream `error` frame) is final; anything else may have reached it. */
function isAnswered(reason: unknown): boolean {
  if (reason instanceof RileyTurnError) return true
  const status = (reason as { status?: unknown } | null)?.status
  return typeof status === 'number'
}

function isStream(delivery: ChatDelivery): delivery is AsyncIterable<RileyChatStreamEvent> {
  return typeof delivery === 'object' && delivery !== null && Symbol.asyncIterator in delivery
}

function upsertCard(cards: RileyCard[], card: RileyCard): RileyCard[] {
  const at = cards.findIndex((candidate) => candidate.id === card.id)
  if (at === -1) return [...cards, card]
  return cards.map((candidate, index) => (index === at ? card : candidate))
}

export function replaceCard(thread: RileyChatThread, card: RileyCard): RileyChatThread {
  return { ...thread, cards: upsertCard(thread.cards, card) }
}

export function setChatThread(key: string, thread: RileyChatThread): void {
  chats.set(key, (e) => (e.thread === thread ? e : { ...e, thread }))
}

export function updateChatThread(key: string, update: (thread: RileyChatThread) => RileyChatThread): void {
  chats.set(key, (e) => (e.thread ? { ...e, thread: update(e.thread) } : e))
}

// --- a pending turn survives a reload in sessionStorage (this tab only; never shared) -------------------------------
const PENDING_PREFIX = 'patchbay:riley:pending:'
const RECOVER_WINDOW_MS = 4 * 60_000

function session(): Storage | null {
  try {
    return globalThis.sessionStorage ?? null
  } catch {
    return null
  }
}

function persistPending(key: string, pending: PendingTurn | null): void {
  try {
    if (pending) session()?.setItem(PENDING_PREFIX + key, JSON.stringify(pending))
    else session()?.removeItem(PENDING_PREFIX + key)
  } catch {
    /* convenience only */
  }
}

function persistedPending(key: string): PendingTurn | null {
  try {
    const raw = session()?.getItem(PENDING_PREFIX + key)
    if (!raw) return null
    const p = JSON.parse(raw) as PendingTurn
    return p && typeof p.startedAt === 'number' && Date.now() - p.startedAt < RECOVER_WINDOW_MS ? p : null
  } catch {
    return null
  }
}

// --- the turn ----------------------------------------------------------------------------------------------------

export interface TurnTransport {
  send: (text: string, clientMessageId: string) => Promise<ChatDelivery>
  /** The stored thread (GET). Without it a lost stream is reported as an error. */
  recover?: () => Promise<RileyChatThread>
}

export interface PollTiming {
  intervalMs: number
  timeoutMs: number
}

export const POLL: PollTiming = { intervalMs: 4_000, timeoutMs: 3 * 60_000 }

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

function answered(thread: RileyChatThread, pending: PendingTurn): boolean {
  return thread.messages.length > pending.baseline && thread.messages.slice(pending.baseline).some((m) => m.role === 'riley')
}

function finish(key: string, patch: Partial<RileyChatEntry>): void {
  persistPending(key, null)
  chats.set(key, (e) => ({ ...e, pending: null, streamText: '', toolSteps: [], recovering: false, ...patch }))
}

async function pollForAnswer(key: string, pending: PendingTurn, recover: () => Promise<RileyChatThread>): Promise<boolean> {
  chats.set(key, (e) => ({ ...e, recovering: true }))
  const until = pending.startedAt + POLL.timeoutMs
  while (Date.now() < until) {
    await sleep(POLL.intervalMs)
    try {
      const thread = await recover()
      if (answered(thread, pending)) {
        finish(key, { thread })
        return true
      }
    } catch {
      /* keep polling until the window closes */
    }
  }
  return false
}

function applyFrame(
  key: string,
  frame: RileyChatStreamEvent,
  acc: {
    reply: string
    replyId: string
    citations: RileyChatMessage['citations']
    resultTables: RileyChatMessage['result_tables']
    reportCards: RileyChatMessage['report_cards']
    usage: RileyChatMessage['usage']
  },
) {
  if (frame.event === 'turn.started') acc.replyId = frame.data.message_id ?? acc.replyId
  if (frame.event === 'tool.call') {
    const { seq, name, args_summary: request } = frame.data
    const step: ToolStep = {
      seq,
      tool: name,
      label: summarizeToolStep(name, request),
      ok: true,
      detail: rawStepDetail(request),
    }
    chats.set(key, (e) => ({ ...e, toolSteps: [...e.toolSteps.filter((candidate) => candidate.seq !== seq), step] }))
  }
  if (frame.event === 'tool.result') {
    const { seq, ok, summary } = frame.data
    chats.set(key, (e) => {
      const call = e.toolSteps.find((step) => step.seq === seq)
      const request = call?.detail?.replace(/^Request: /, '')
      const label = summarizeToolStep(call?.tool ?? '', request, summary)
      const next: ToolStep = {
        seq,
        tool: call?.tool,
        label: ok ? label : failedToolStepLabel(call?.label ?? label),
        ok,
        detail: rawStepDetail(request, summary),
      }
      return {
        ...e,
        toolSteps: call ? e.toolSteps.map((step) => (step === call ? next : step)) : [...e.toolSteps, next],
      }
    })
  }
  if (frame.event === 'delta') {
    acc.reply += frame.data.text
    chats.set(key, (e) => ({ ...e, streamText: acc.reply }))
  }
  if (frame.event === 'card') updateChatThread(key, (t) => replaceCard(t, frame.data.card))
  if (frame.event === 'turn.completed') {
    acc.reply = frame.data.reply
    acc.replyId = frame.data.message_id
    acc.citations = frame.data.citations ?? []
    acc.resultTables = frame.data.result_tables ?? []
    acc.reportCards = frame.data.report_cards ?? []
    acc.usage = frame.data.usage ?? null
    if (frame.data.walkthrough !== undefined) announceWalkthrough(frame.data.walkthrough)
  }
  if (frame.event === 'error') throw new RileyTurnError(frame.data.code, frame.data.detail)
}

/** True while a turn for `key` is in flight (streaming or being recovered). */
export function chatBusy(key: string): boolean {
  return chats.get(key).pending !== null
}

/**
 * Send one message. Runs outside React: the component that started it may unmount; whoever subscribes to `key`
 * sees the same pending row, stream and answer. Returns when the turn is settled.
 */
export async function sendChatTurn(key: string, text: string, transport: TurnTransport): Promise<void> {
  const entry = chats.get(key)
  if (!entry.thread || entry.pending || running.has(key)) return
  running.add(key)
  const clientMessageId = globalThis.crypto.randomUUID()
  const pending: PendingTurn = {
    text,
    clientMessageId,
    threadId: entry.thread.id,
    startedAt: Date.now(),
    baseline: entry.thread.messages.length,
  }
  const optimistic: RileyChatMessage = {
    id: clientMessageId,
    role: 'user',
    author: 'You',
    content_md: text,
    citations: [],
    card_ids: [],
    created_at: new Date(pending.startedAt).toISOString(),
  }
  chats.set(key, (e) => ({
    ...e,
    thread: e.thread ? { ...e.thread, messages: [...e.thread.messages, optimistic] } : e.thread,
    pending,
    streamText: '',
    toolSteps: [],
    error: null,
    recovering: false,
  }))
  persistPending(key, pending)
  const acc = {
    reply: '',
    replyId: globalThis.crypto.randomUUID() as string,
    citations: [] as RileyChatMessage['citations'],
    resultTables: [] as RileyChatMessage['result_tables'],
    reportCards: [] as RileyChatMessage['report_cards'],
    usage: null as RileyChatMessage['usage'],
  }
  try {
    const delivery = await transport.send(text, clientMessageId)
    if (isStream(delivery)) {
      for await (const frame of delivery) applyFrame(key, frame, acc)
      if (acc.reply) {
        const toolSteps = chats.get(key).toolSteps
        const message: RileyChatMessage = {
          id: acc.replyId,
          role: 'riley',
          content_md: acc.reply,
          citations: acc.citations,
          result_tables: acc.resultTables,
          report_cards: acc.reportCards,
          card_ids: [],
          created_at: new Date().toISOString(),
          usage: acc.usage,
          tool_steps: toolSteps.length ? toolSteps : undefined,
        }
        updateChatThread(key, (t) => ({ ...t, messages: [...t.messages, message] }))
      }
      finish(key, {})
    } else {
      const degraded = delivery.riley_state && delivery.riley_state !== 'ok'
      finish(key, { thread: delivery, error: degraded ? (delivery.riley_detail ?? 'Riley did not receive this message.') : null })
    }
  } catch (reason) {
    const message = reason instanceof Error ? reason.message : 'Riley could not finish this turn.'
    if (!isAnswered(reason) && transport.recover && (await pollForAnswer(key, pending, transport.recover))) return
    finish(key, { error: message })
  } finally {
    running.delete(key)
  }
}

/**
 * After a reload (the stream died with the page) or a remount that found no running turn: if this tab had a turn in
 * flight for `key`, show it as pending again and poll the stored thread for its answer.
 */
export async function resumeChatTurn(key: string, recover: () => Promise<RileyChatThread>): Promise<void> {
  if (running.has(key) || chats.get(key).pending) return
  const pending = persistedPending(key)
  const thread = chats.get(key).thread
  if (!pending || !thread || thread.id !== pending.threadId) {
    if (!pending) persistPending(key, null)
    return
  }
  if (answered(thread, pending)) {
    persistPending(key, null)
    return
  }
  running.add(key)
  chats.set(key, (e) => ({ ...e, pending, error: null }))
  try {
    if (!(await pollForAnswer(key, pending, recover))) finish(key, { error: 'Riley did not answer in time; ask again.' })
  } finally {
    running.delete(key)
  }
}

// --- per-person conveniences (this browser): collapsed hypothesis, hidden cards (patchbay#198) ---------------------
const PREFS_PREFIX = 'patchbay:riley:prefs:'
const SEEN_CAP = 400

export interface RileyPrefs {
  /** An explicit choice; `undefined` means "expanded on a case's first view, collapsed after". */
  summaryCollapsed?: boolean
  seenCases: string[]
  hiddenCards: string[]
}

const NO_PREFS: RileyPrefs = { seenCases: [], hiddenCards: [] }
const prefs = createKeyedStore<RileyPrefs>(NO_PREFS)

function loadPrefs(user: string): RileyPrefs {
  if (!prefs.has(user)) {
    let loaded = NO_PREFS
    try {
      const raw = safeStorage.getItem(PREFS_PREFIX + user)
      if (raw) {
        const p = JSON.parse(raw) as Partial<RileyPrefs>
        loaded = {
          summaryCollapsed: typeof p.summaryCollapsed === 'boolean' ? p.summaryCollapsed : undefined,
          seenCases: Array.isArray(p.seenCases) ? p.seenCases.filter((x) => typeof x === 'string') : [],
          hiddenCards: Array.isArray(p.hiddenCards) ? p.hiddenCards.filter((x) => typeof x === 'string') : [],
        }
      }
    } catch {
      /* a corrupt value is the defaults */
    }
    prefs.set(user, () => loaded)
  }
  return prefs.get(user)
}

export function updateRileyPrefs(user: string, update: (p: RileyPrefs) => RileyPrefs): void {
  loadPrefs(user)
  prefs.set(user, update)
  safeStorage.setItem(PREFS_PREFIX + user, JSON.stringify(prefs.get(user)))
}

export function useRileyPrefs(user: string): RileyPrefs {
  loadPrefs(user)
  return prefs.use(user)
}

export function resetRileyPrefs(): void {
  prefs.reset()
  firstViewedThisTab.clear()
}

/**
 * The hypothesis card's collapsed state for one case: the person's explicit choice when there is one; otherwise open
 * on the first view of a case and collapsed on later views. Marks the case seen after the first render.
 */
const firstViewedThisTab = new Set<string>()

export function useSummaryCollapsed(user: string, caseId: string): [boolean, (collapsed: boolean) => void] {
  const p = useRileyPrefs(user)
  // A case first seen in this tab stays open for the tab's life, so expanding the rail (a remount) does not fold it.
  if (!p.seenCases.includes(caseId)) firstViewedThisTab.add(`${user}:${caseId}`)
  React.useEffect(() => {
    if (p.seenCases.includes(caseId)) return
    updateRileyPrefs(user, (cur) => ({ ...cur, seenCases: [...cur.seenCases.filter((c) => c !== caseId), caseId].slice(-SEEN_CAP) }))
  }, [user, caseId, p.seenCases])
  const collapsed = p.summaryCollapsed ?? !firstViewedThisTab.has(`${user}:${caseId}`)
  const set = React.useCallback((next: boolean) => updateRileyPrefs(user, (cur) => ({ ...cur, summaryCollapsed: next })), [user])
  return [collapsed, set]
}

export function hideCard(user: string, cardId: string): void {
  updateRileyPrefs(user, (p) => (p.hiddenCards.includes(cardId) ? p : { ...p, hiddenCards: [...p.hiddenCards, cardId].slice(-SEEN_CAP) }))
}

export function unhideCards(user: string, cardIds: string[]): void {
  updateRileyPrefs(user, (p) => ({ ...p, hiddenCards: p.hiddenCards.filter((id) => !cardIds.includes(id)) }))
}
